import React, { useState, useEffect, useMemo } from 'react';
import { Search, ChevronDown, Minus, Plus, ChefHat, Star, ShoppingBag, X, Loader, Home, Menu, Receipt, Mail, LogOut } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useSearchParams, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081';

const API_ORIGIN = (() => {
  try {
    return new URL(API_URL).origin;
  } catch (_error) {
    return API_URL.replace(/\/+$/, '');
  }
})();

const CURRENT_ORDER_TYPE = 'DINE_IN';

const normalizeOrderTypeKey = (value) => String(value ?? '')
  .trim()
  .replace(/[-\s]+/g, '_')
  .toUpperCase();

const getEffectiveOrderTypePrice = (basePrice, orderTypePrices = [], orderType = CURRENT_ORDER_TYPE) => {
  const fallbackPrice = parseFloat(basePrice) || 0;
  if (!Array.isArray(orderTypePrices) || orderTypePrices.length === 0) {
    return fallbackPrice;
  }

  const normalizedOrderType = normalizeOrderTypeKey(orderType);
  const match = orderTypePrices.find(entry => {
    const entryOrderType = normalizeOrderTypeKey(entry?.orderType ?? entry?.order_type ?? entry?.type);
    return entryOrderType === normalizedOrderType;
  });

  if (!match) {
    return fallbackPrice;
  }

  const resolvedPrice = parseFloat(
    match.price ?? match.orderTypePrice ?? match.order_type_price ?? match.value
  );

  return Number.isFinite(resolvedPrice) ? resolvedPrice : fallbackPrice;
};

const isItemAvailableForOrderType = (item, orderType = CURRENT_ORDER_TYPE) => {
  if (!item) return false;
  if (item.isAvailable === false || item.is_available === false) {
    return false;
  }

  const normalizedOrderType = normalizeOrderTypeKey(orderType);
  const orderTypePrices = Array.isArray(item.orderTypePrices) ? item.orderTypePrices : [];
  if (orderTypePrices.length > 0) {
    const match = orderTypePrices.find(entry => {
      const entryOrderType = normalizeOrderTypeKey(entry?.orderType ?? entry?.order_type ?? entry?.type);
      return entryOrderType === normalizedOrderType;
    });

    if (match && (match.isActive === false || match.is_active === false)) {
      return false;
    }
  }
  return true;
};

const buildKotSpecialRequestJson = (cartItem) => JSON.stringify({
  orderType: CURRENT_ORDER_TYPE,
  note: cartItem.specialNote?.trim() || '',
  addons: (cartItem.selectedAddons || []).map(addon => ({
    addonId: addon.addonId,
    addonName: addon.addonName,
    price: parseFloat(addon.price ?? addon.addonPrice ?? 0) || 0
  })),
  modifiers: (cartItem.selectedModifiers || []).map(modifier => ({
    optionId: modifier.optionId,
    groupId: modifier.groupId,
    groupName: modifier.groupName,
    optionName: modifier.optionName,
    price: parseFloat(modifier.price ?? modifier.optionPrice ?? 0) || 0
  }))
});

const resolveMediaUrl = (url) => {
  if (!url) return '';
  const raw = String(url).trim();
  if (!raw) return '';
  if (/^(data:|blob:)/i.test(raw)) return raw;
  try {
    if (/^https?:\/\//i.test(raw)) {
      const parsed = new URL(raw);
      const isLoopback = ['localhost', '127.0.0.1', '::1'].includes(parsed.hostname);
      const apiPort = (() => { try { return new URL(API_URL).port || ''; } catch (e) { return ''; } })();
      if (isLoopback && parsed.port && parsed.port !== apiPort) {
        return `${API_ORIGIN}${parsed.pathname}${parsed.search}`;
      }
      return raw;
    }
  } catch (_error) {
    return '';
  }
  const normalizedPath = raw.startsWith('/') ? raw : `/${raw}`;
  return `${API_ORIGIN}${normalizedPath}`;
};

// --- Components ---

const VegIcon = () => (
  <div className="w-4 h-4 border border-green-600 p-0.5 flex items-center justify-center bg-white">
    <div className="w-2 h-2 rounded-full bg-green-600"></div>
  </div>
);

const NonVegIcon = () => (
  <div className="w-4 h-4 border border-red-600 p-0.5 flex items-center justify-center bg-white">
    <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-b-[6px] border-b-red-600"></div>
  </div>
);

const AddButton = ({ count, onAdd, onRemove }) => {
  if (count > 0) {
    return (
      <div className="flex items-center justify-between w-24 h-10 bg-white border-2 border-orange-500 rounded-lg shadow-md overflow-hidden">
        <button 
          onClick={onRemove}
          className="w-8 h-full flex items-center justify-center text-orange-600 hover:bg-orange-50 active:bg-orange-100 transition-colors font-bold"
        >
          <Minus size={16} strokeWidth={3} />
        </button>
        <span className="text-sm font-extrabold text-orange-600">{count}</span>
        <button 
          onClick={onAdd}
          className="w-8 h-full flex items-center justify-center text-orange-600 hover:bg-orange-50 active:bg-orange-100 transition-colors font-bold"
        >
          <Plus size={16} strokeWidth={3} />
        </button>
      </div>
    );
  }
  
  return (
    <button 
      onClick={onAdd}
      className="w-24 h-10 bg-gradient-to-r from-orange-500 to-red-500 border-2 border-orange-600 rounded-lg text-white font-extrabold text-sm shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all flex items-center justify-center uppercase tracking-wide"
    >
      Add
    </button>
  );
};

// --- Customization Modal Component ---
const CustomizationModal = ({ item, onConfirm, onCancel }) => {
  const [qty, setQty] = useState(1);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [specialNote, setSpecialNote] = useState('');

  const normalizeContextKey = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

  const buildParentContextKeys = (parentOption) => {
    if (!parentOption) return new Set();
    const optionId = parentOption?.optionId || parentOption?.legacyAddonId || parentOption?.addonId;
    const rawKeys = [
      optionId ? `optionid:${optionId}` : null,
      optionId ? `id:${optionId}` : null,
      optionId ? `${optionId}` : null,
      parentOption?.addonName || parentOption?.optionName || null
    ].filter(Boolean);

    return new Set(rawKeys.map((key) => normalizeContextKey(key)).filter(Boolean));
  };

  const matchesDependentContext = (addon, parentOption) => {
    const contextKey = normalizeContextKey(addon?.contextKey);
    if (!contextKey) return true;
    if (!parentOption) return false;
    return buildParentContextKeys(parentOption).has(contextKey);
  };

  const addons = Array.isArray(item?.addons) ? item.addons : [];
  const modifierGroups = Array.isArray(item?.modifierGroups) ? item.modifierGroups : [];

  const toggleAddon = (addonId) => {
    setSelectedAddons(prev =>
      prev.includes(addonId) ? prev.filter(id => id !== addonId) : [...prev, addonId]
    );
  };

  const toggleModifier = (groupId, optionId, maxSelection) => {
    setSelectedModifiers(prev => {
      const current = prev[groupId] || [];
      if (maxSelection === 1) {
        return { ...prev, [groupId]: [optionId] };
      }
      if (current.includes(optionId)) {
        return { ...prev, [groupId]: current.filter(id => id !== optionId) };
      }
      if (current.length < maxSelection) {
        return { ...prev, [groupId]: [...current, optionId] };
      }
      return prev;
    });
  };

  const addonTotal = addons
    .filter(a => selectedAddons.includes(a.addonId))
    .reduce((sum, a) => sum + getEffectiveOrderTypePrice(a.addonPrice ?? a.price ?? 0, a.orderTypePrices), 0);

  const modifierTotal = modifierGroups
    .flatMap(g => (g.options || []).filter(o => (selectedModifiers[g.groupId] || []).includes(o.optionId)))
    .reduce((sum, o) => sum + getEffectiveOrderTypePrice(o.optionPrice ?? o.price ?? 0, o.orderTypePrices), 0);

  const baseEffectivePrice = getEffectiveOrderTypePrice(item.price ?? item.basePrice ?? 0, item.orderTypePrices);
  const unitPrice = baseEffectivePrice + addonTotal + modifierTotal;
  const total = unitPrice * qty;

  const visibleModifierGroups = modifierGroups.filter(g => {
    if (!g) return false;
    if (!g.dependsOnGroupId) return true;
    const parentSelected = selectedModifiers[g.dependsOnGroupId] || [];
    return parentSelected.length > 0;
  });

  // compute a sensible 'FROM' price when base is zero using first visible modifier group's options
  const firstGroupForFrom = (visibleModifierGroups && visibleModifierGroups.length > 0)
    ? visibleModifierGroups[0]
    : (modifierGroups && modifierGroups.length > 0 ? modifierGroups[0] : null);
  const minOptionFromPrice = (firstGroupForFrom && Array.isArray(firstGroupForFrom.options) && firstGroupForFrom.options.length > 0)
    ? Math.min(...firstGroupForFrom.options.map(o => getEffectiveOrderTypePrice(o.optionPrice ?? o.price ?? 0, o.orderTypePrices)))
    : 0;
  const shouldShowFrom = baseEffectivePrice === 0 && addonTotal === 0 && modifierTotal === 0 && minOptionFromPrice > 0;

  const isValid = visibleModifierGroups.every(g => {
    if (!g.isRequired) return true;
    const selected = selectedModifiers[g.groupId] || [];
    return selected.length >= (g.minSelection || 1);
  });

  const handleConfirm = () => {
    const selAddons = addons.filter(a => selectedAddons.includes(a.addonId));
    const selModifiers = visibleModifierGroups.flatMap(g =>
      (g.options || [])
        .filter(o => (selectedModifiers[g.groupId] || []).includes(o.optionId))
        .map(o => ({
          optionId: o.optionId,
          groupId: g.groupId,
          groupName: g.groupName,
          optionName: o.optionName,
          optionPrice: getEffectiveOrderTypePrice(o.optionPrice ?? o.price ?? 0, o.orderTypePrices),
          price: getEffectiveOrderTypePrice(o.optionPrice ?? o.price ?? 0, o.orderTypePrices),
          orderTypePrices: Array.isArray(o.orderTypePrices) ? o.orderTypePrices : []
        }))
    );
    onConfirm({
      item,
      qty,
      selectedAddons: selAddons.map(addon => ({
        ...addon,
        price: getEffectiveOrderTypePrice(addon.addonPrice, addon.orderTypePrices),
        orderTypePrices: Array.isArray(addon.orderTypePrices) ? addon.orderTypePrices : []
      })),
      selectedModifiers: selModifiers,
      specialNote: specialNote.trim(),
      unitPrice,
      total
    });
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-md p-0 sm:p-4 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-md h-[85vh] sm:h-auto sm:max-h-[85vh] rounded-t-3xl sm:rounded-3xl flex flex-col shadow-2xl animate-in slide-in-from-bottom-10 duration-300">
        <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-orange-50 to-red-50 rounded-t-3xl">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{item.name}</h3>
            <p className="text-sm text-orange-600 font-semibold">
              {shouldShowFrom ? `FROM ₹${minOptionFromPrice}` : `₹${unitPrice}`}
            </p>
          </div>
          <button onClick={onCancel} className="p-2 bg-white rounded-full hover:bg-gray-100 shadow-sm">
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Modifiers */}
          {visibleModifierGroups.map(group => {
            const isSingle = (group.maxSelection || 1) === 1;
            const selected = selectedModifiers[group.groupId] || [];
            const selectedParentOption = group.dependsOnGroupId
              ? (modifierGroups.find(pg => String(pg.groupId) === String(group.dependsOnGroupId))?.options || [])
                  .find(popt => (selectedModifiers[group.dependsOnGroupId] || []).includes(popt.optionId)) || null
              : null;
            const contextFilteredOptions = (group.options || []).filter((addon) =>
              matchesDependentContext(addon, selectedParentOption)
            );

            if (group.dependsOnGroupId && contextFilteredOptions.length === 0) {
              return null;
            }
            return (
              <div key={group.groupId} className="bg-gray-50 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-bold text-sm text-gray-800">
                    {group.groupName}
                    {group.dependsOnGroupId && selectedParentOption ? ` [${selectedParentOption?.optionName || selectedParentOption?.addonName || ''}]` : ''}
                  </h4>
                  {group.isRequired && <span className="text-red-500 text-xs">*Required</span>}
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  {isSingle ? 'Select one' : `Select up to ${group.maxSelection}`}
                </p>
                <div className="space-y-2">
                  {contextFilteredOptions.map(option => (
                    <label key={option.optionId} className="flex items-center gap-3 p-2 bg-white rounded-lg cursor-pointer hover:bg-orange-50 transition-colors">
                      <input
                        type={isSingle ? 'radio' : 'checkbox'}
                        name={`group-${group.groupId}`}
                        checked={selected.includes(option.optionId)}
                        onChange={() => toggleModifier(group.groupId, option.optionId, group.maxSelection || 1)}
                        className="w-4 h-4 text-orange-600 accent-orange-600"
                      />
                      <span className="flex-1 text-sm text-gray-700">{option.optionName}</span>
                      {getEffectiveOrderTypePrice(option.optionPrice ?? option.price ?? 0, option.orderTypePrices) > 0 && (
                        <span className="text-xs font-semibold text-orange-600">+₹{getEffectiveOrderTypePrice(option.optionPrice ?? option.price ?? 0, option.orderTypePrices)}</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Addons */}
          {addons.length > 0 && (
            <div className="bg-gray-50 rounded-xl p-3">
              <h4 className="font-bold text-sm text-gray-800 mb-2">Add-ons</h4>
              <div className="space-y-2">
                {addons.map(addon => (
                  <label key={addon.addonId} className="flex items-center gap-3 p-2 bg-white rounded-lg cursor-pointer hover:bg-orange-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedAddons.includes(addon.addonId)}
                      onChange={() => toggleAddon(addon.addonId)}
                      className="w-4 h-4 text-orange-600 accent-orange-600"
                    />
                    <span className="flex-1 text-sm text-gray-700">{addon.addonName}</span>
                    <span className="text-xs font-semibold text-orange-600">+₹{getEffectiveOrderTypePrice(addon.addonPrice ?? addon.price ?? 0, addon.orderTypePrices)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Special Note */}
          <div className="bg-gray-50 rounded-xl p-3">
            <h4 className="font-bold text-sm text-gray-800 mb-2">Special Instructions</h4>
            <textarea
              value={specialNote}
              onChange={(e) => setSpecialNote(e.target.value)}
              placeholder="Any special requests?"
              className="w-full p-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none"
              rows={2}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-white rounded-b-3xl">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg text-gray-700 font-bold hover:bg-gray-200"
              >
                <Minus size={14} strokeWidth={3} />
              </button>
              <span className="text-lg font-bold text-gray-900 w-6 text-center">{qty}</span>
              <button
                onClick={() => setQty(qty + 1)}
                className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg text-gray-700 font-bold hover:bg-gray-200"
              >
                <Plus size={14} strokeWidth={3} />
              </button>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-xl font-bold text-orange-600">₹{total}</p>
            </div>
          </div>
          <button
            onClick={handleConfirm}
            disabled={!isValid}
            className={`w-full py-3 rounded-xl font-bold text-sm shadow-lg transition-all ${
              isValid
                ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white hover:scale-[1.02] active:scale-[0.98]'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {!isValid ? 'Please select required options' : `Add to Cart • ₹${total}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function QRMenuOrder() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const restaurantId = searchParams.get('r');
  const tableId = searchParams.get('t');

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  // ── Permission gate (CREATE_ORDER) ───────────────────────────────────────
  // Permissions are stored in localStorage at login — no extra fetch needed.
  // Owners always bypass: they get role 'SUPER_ADMIN' or 'OWNER' at login.
  const _storedRole = localStorage.getItem('role') || '';
  const _storedPermissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const canCreateOrder = _storedRole === 'SUPER_ADMIN' || _storedRole === 'OWNER' || _storedPermissions.includes('CREATE_ORDER');

  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [tableInfo, setTableInfo] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper function to calculate tax dynamically based on restaurant settings
  const calculateTax = (subtotal) => {
    if (!restaurantInfo?.taxSettings?.isTaxEnabled) return 0;
    const taxPercentage = restaurantInfo.taxSettings.taxPercentage || 0;
    return Math.round(subtotal * (taxPercentage / 100));
  };

  const isMrpCartItem = (cartItem) => Boolean(
    cartItem?.categoryIsMrp ?? cartItem?.category_is_mrp ?? cartItem?.isMrp ?? cartItem?.is_mrp
  );

  const getTaxableCartTotal = () => cart.reduce((total, cartItem) => {
    if (isMrpCartItem(cartItem)) {
      return total;
    }
    return total + getCartItemTotal(cartItem);
  }, 0);

  // Helper function to get tax percentage for display
  const getTaxPercentageText = () => {
    if (!restaurantInfo?.taxSettings?.isTaxEnabled) return "0";
    return restaurantInfo.taxSettings.taxPercentage || 0;
  };

  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [vegOnly, setVegOnly] = useState(false);
  const [activeCategory, setActiveCategory] = useState("");
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showCategoryMenuModal, setShowCategoryMenuModal] = useState(false);
  const [customizationModal, setCustomizationModal] = useState(null);
  
  // Customer form and OTP state
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [showOTPForm, setShowOTPForm] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [customerData, setCustomerData] = useState({
    name: '',
    phone: '',
    email: '',
    numberOfPersons: 1
  });
  const [customerId, setCustomerId] = useState(null);
  const [otp, setOTP] = useState('');
  const [isOTPSending, setIsOTPSending] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0); // Cooldown timer in seconds
  const [isOrderPlacing, setIsOrderPlacing] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  
  // Customer session management
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [existingOrder, setExistingOrder] = useState(null);
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const [sessionCustomerData, setSessionCustomerData] = useState(null);

  // Check for existing table session (for staff orders)
  const checkTableSession = async () => {
    if (!tableId || !restaurantId) return false;
    
    try {
      console.log("🔍 Checking table session for tableId:", tableId);
      const token = localStorage.getItem('staffToken');
      const response = await axios.get(
        `${API_URL}/api/billing-pos/restaurants/${restaurantId}/dine-in/tables-by-id/${tableId}/active-order`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data && response.data.hasActiveOrder && response.data.orderId) {
        console.log("✅ Found active table session:", response.data);
        
        // Map the response to match expected format
        const orderData = {
          orderId: response.data.orderId,
          orderNumber: response.data.billNo || response.data.orderNumber,
          orderStatus: response.data.orderStatus,
          grandTotal: response.data.grandTotal,
          customerId: response.data.customerId || null
        };
        
        // Store session in localStorage for persistence
        localStorage.setItem('activeTableOrderId', response.data.orderId.toString());
        localStorage.setItem('activeOrderNumber', orderData.orderNumber || '');
        if (response.data.customerId) {
          localStorage.setItem('customerId', response.data.customerId.toString());
        }
        
        setHasActiveSession(true);
        setExistingOrder(orderData);
        setCustomerId(response.data.customerId || null);
        return true;
      }
      
      console.log("ℹ️ No active order found on this table");
      // Clear localStorage if no active order
      localStorage.removeItem('activeTableOrderId');
      localStorage.removeItem('activeOrderNumber');
      return false;
    } catch (error) {
      console.log("❌ Error checking table session:", error.message);
      return false;
    }
  };

  // Check for existing customer session
  const checkCustomerSession = async (phone) => {
    console.log("🔍 Checking customer session for phone:", phone);
    if (!phone || phone.length < 10) return false;
    
    try {
      setIsCheckingSession(true);
      const token = localStorage.getItem('staffToken');
      const response = await axios.get(
        `${API_URL}/api/restaurants/${restaurantId}/qr-orders/check-customer-session`,
        { 
          params: { 
            phone: phone,
            tableId: parseInt(tableId),
            restaurantId: parseInt(restaurantId)
          },
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log("📡 Session check response:", response.data);
      
      if (response.data.hasActiveOrder) {
        console.log("✅ Active session found, setting state:");
        console.log("- Order:", response.data.existingOrder);
        console.log("- Customer Data:", response.data.customerData);
        
        // Store session data in localStorage for persistence
        localStorage.setItem('customerPhone', phone);
        localStorage.setItem('customerId', response.data.customerId?.toString() || '');
        localStorage.setItem('customerName', response.data.customerData?.name || '');
        if (response.data.existingOrder?.orderNumber) {
          localStorage.setItem('activeOrderNumber', response.data.existingOrder.orderNumber);
        }
        
        setHasActiveSession(true);
        setExistingOrder(response.data.existingOrder || response.data.order); 
        setSessionCustomerData(response.data.customerData || response.data);
        setCustomerId(response.data.customerId);
        
        console.log("🎯 State set - hasActiveSession: true, customerId:", response.data.customerId);
        console.log("💾 Session data saved to localStorage");
        return true;
      } else {
        console.log("❌ No active session found");
      }
      return false;
    } catch (error) {
      console.error('Error checking session:', error);
      return false;
    } finally {
      setIsCheckingSession(false);
    }
  };

  // Add items to existing order (for session customers)
  const addToExistingOrder = async () => {
    console.log("🔥 addToExistingOrder called!", {
      cartTotalItems,
      customerId,
      tableId,
      restaurantId,
      cart,
      menuItems: menuItems.length
    });
    
    if (cartTotalItems === 0) {
      console.log("❌ Cart is empty");
      toast.error('Please add items to cart first');
      return;
    }
    
    setIsOrderPlacing(true);
    
    try {
      const orderItems = cart.map(cartItem => ({
        itemId: cartItem.itemId,
        itemName: cartItem.name,
        quantity: cartItem.qty,
        price: getCartItemUnitPrice(cartItem),
        specialNote: buildKotSpecialRequestJson(cartItem),
        categoryIsMrp: Boolean(cartItem.categoryIsMrp ?? cartItem.category_is_mrp ?? cartItem.isMrp ?? cartItem.is_mrp)
      }));

      const addItemsData = {
        customerId: customerId,
        tableId: parseInt(tableId),
        items: orderItems
      };

      console.log("📤 Sending request to backend:", addItemsData);
      console.log("🎯 API URL:", `${API_URL}/api/restaurants/${restaurantId}/qr-orders/add-to-existing-order`);

      const token = localStorage.getItem('staffToken');
      const response = await axios.post(
        `${API_URL}/api/restaurants/${restaurantId}/qr-orders/add-to-existing-order`,
        addItemsData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log("✅ Backend response:", response.data);

      if (response.data.success) {
        console.log("✅ Items added successfully, updating state and localStorage");
        
        setOrderResult(response.data);
        setExistingOrder(response.data.order); // Update existing order data
        
        // Update localStorage with latest order info
        if (response.data.order?.orderNumber) {
          localStorage.setItem('activeOrderNumber', response.data.order.orderNumber);
        }
        
        setCart([]);
        setIsCartOpen(false); // Close cart modal after successful add
        toast.success(`🎉 Items added to ${response.data.order?.orderNumber || 'your order'} successfully!`, { duration: 5000 });
      } else {
        console.log("❌ Backend returned error:", response.data);
        toast.error(response.data.error || 'Failed to add items');
      }
      
    } catch (error) {
      console.error('❌ Frontend error adding items:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to add items. Please try again.');
      }
    } finally {
      setIsOrderPlacing(false);
    }
  };

  // Modified place order function for staff - customer details optional
  const proceedToOrder = async () => {
    if (cartTotalItems === 0) {
      toast.error('Please add items to cart first');
      return;
    }
    
    // For staff orders, show customer form but make it skippable
    setIsCartOpen(false);
    setShowCustomerForm(true);
  };

  // Fetch restaurant and menu data
  useEffect(() => {
    const fetchData = async () => {
      if (!restaurantId || !tableId) {
        toast.error('Invalid QR code. Restaurant or Table ID missing.');
        return;
      }

      try {
        setLoading(true);
        const token = localStorage.getItem('staffToken');
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // Fetch restaurant details
        const restaurantRes = await axios.get(
          `${API_URL}/api/restaurants/${restaurantId}/public-info`,
          { headers }
        );
        setRestaurantInfo(restaurantRes.data);

        // Fetch table details
        const tableRes = await axios.get(
          `${API_URL}/api/restaurants/${restaurantId}/tables/${tableId}`,
          { headers }
        );
        setTableInfo(tableRes.data);

        // Fetch menu items with customizations
        let productsData = [];
        try {
          const customizationsRes = await axios.get(
            `${API_URL}/api/billing-pos/restaurants/${restaurantId}/available-items-with-customizations`,
            { headers }
          );
          productsData = Array.isArray(customizationsRes?.data?.items)
            ? customizationsRes.data.items
            : Array.isArray(customizationsRes?.data?.data)
              ? customizationsRes.data.data
              : [];
        } catch (customizationError) {
          console.warn('Failed to load items with customizations, falling back:', customizationError?.message);
          const fallbackRes = await axios.get(
            `${API_URL}/api/restaurants/${restaurantId}/menu/items`,
            { headers }
          );
          productsData = Array.isArray(fallbackRes.data) ? fallbackRes.data : [];
        }
        
        // Transform backend data to frontend format
        const transformedItems = productsData.map(item => ({
          id: item.itemId ?? item.item_id ?? item.id,
          name: item.itemName ?? item.item_name ?? item.name,
          basePrice: parseFloat(item.price ?? item.basePrice ?? 0),
          price: getEffectiveOrderTypePrice(item.price ?? item.basePrice ?? 0, item.orderTypePrices),
          orderTypePrices: Array.isArray(item.orderTypePrices) ? item.orderTypePrices : [],
          category: item.categoryName ?? item.category_name ?? item.category ?? 'Uncategorized',
          categoryIsMrp: Boolean(item.categoryIsMrp ?? item.category_is_mrp ?? item.isMrp ?? item.is_mrp ?? false),
          isVeg: (item.itemType ?? item.item_type) === 'VEG',
          description: item.description || '',
          imageUrl: item.imageUrl ?? item.image_url ?? item.image ?? '',
          isAvailable: item.isAvailable !== false && item.is_available !== false,
          isFeatured: item.isFeatured === true || item.is_featured === true,
          addons: Array.isArray(item.addons) ? item.addons : [],
          modifierGroups: Array.isArray(item.modifierGroups) ? item.modifierGroups : []
        })).filter(item => isItemAvailableForOrderType(item, CURRENT_ORDER_TYPE));
        
        setMenuItems(transformedItems);

        // Extract unique categories
        const uniqueCategories = [...new Set(transformedItems.map(item => item.category))].filter(Boolean);
        setCategories(['All', ...uniqueCategories]);
        setActiveCategory('All');

        setLoading(false);
        
        // CRITICAL: Always check for existing table session first (table-based, no customer required)
        console.log("🔄 Checking for existing table session...");
        const tableSessionFound = await checkTableSession();
        
        // If no table session found, check for customer session (phone-based)
        if (!tableSessionFound) {
          console.log("ℹ️ No table session, checking for customer session...");
          const savedPhone = localStorage.getItem('customerPhone');
          if (savedPhone) {
            await checkCustomerSession(savedPhone);
          }
        } else {
          console.log("✅ Table session restored successfully");
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load menu. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, [restaurantId, tableId]);

  // OTP Cooldown timer countdown
  useEffect(() => {
    if (otpCooldown > 0) {
      const timer = setTimeout(() => setOtpCooldown(otpCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCooldown]);

  // -- Handlers --
  // -- Cart Helpers --
  const hasCustomizations = (item) => {
    return (item?.addons?.length > 0) || (item?.modifierGroups?.length > 0);
  };

  const getCartItemSignature = (cartItem) => {
    const addonSig = (cartItem.selectedAddons || []).map(a => a.addonId).sort().join(',');
    const modifierSig = (cartItem.selectedModifiers || []).map(m => m.optionId).sort().join(',');
    const note = cartItem.specialNote || '';
    return `${cartItem.itemId}|A:${addonSig}|M:${modifierSig}|N:${note}`;
  };

  const formatSpecialNote = (cartItem) => {
    const parts = [];
    if (cartItem.selectedAddons?.length > 0) {
      const names = cartItem.selectedAddons.map(a => `${a.addonName} (+₹${a.price ?? a.addonPrice})`).join(', ');
      parts.push(`Addons: ${names}`);
    }
    if (cartItem.selectedModifiers?.length > 0) {
      const names = cartItem.selectedModifiers.map(m => `${m.groupName}: ${m.optionName} (+₹${m.price ?? m.optionPrice})`).join(', ');
      parts.push(`Modifiers: ${names}`);
    }
    if (cartItem.specialNote?.trim()) {
      parts.push(`Note: ${cartItem.specialNote.trim()}`);
    }
    return parts.join(' | ');
  };

  const getCartItemTotal = (cartItem) => {
    const base = parseFloat(cartItem.price) || 0;
    const addonTotal = (cartItem.selectedAddons || []).reduce((sum, a) => sum + (parseFloat(a.price ?? a.addonPrice ?? 0) || 0), 0);
    const modifierTotal = (cartItem.selectedModifiers || []).reduce((sum, m) => sum + (parseFloat(m.price ?? m.optionPrice ?? 0) || 0), 0);
    return (base + addonTotal + modifierTotal) * (cartItem.qty || 1);
  };

  const getCartItemUnitPrice = (cartItem) => {
    const base = parseFloat(cartItem.price) || 0;
    const addonTotal = (cartItem.selectedAddons || []).reduce((sum, a) => sum + (parseFloat(a.price ?? a.addonPrice ?? 0) || 0), 0);
    const modifierTotal = (cartItem.selectedModifiers || []).reduce((sum, m) => sum + (parseFloat(m.price ?? m.optionPrice ?? 0) || 0), 0);
    return base + addonTotal + modifierTotal;
  };

  // -- Handlers --
  const addToCart = (item, customization = null) => {
    if (!item) return;
    if (customization) {
      const newCartItem = {
        cartItemId: Date.now() + Math.random().toString(36).slice(2),
        itemId: item.id,
        name: item.name,
        price: (Number.isFinite(Number(customization.unitPrice)) && Number(customization.unitPrice) >= 0)
          ? Number(customization.unitPrice)
          : item.price,
        imageUrl: item.imageUrl,
        isVeg: item.isVeg,
          categoryIsMrp: Boolean(item.categoryIsMrp ?? item.category_is_mrp ?? item.isMrp ?? item.is_mrp ?? false),
        qty: customization.qty || 1,
        selectedAddons: customization.selectedAddons || [],
        selectedModifiers: customization.selectedModifiers || [],
        specialNote: customization.specialNote || ''
      };
      setCart(prev => {
        const sig = getCartItemSignature(newCartItem);
        const existingIdx = prev.findIndex(c => getCartItemSignature(c) === sig);
        if (existingIdx >= 0) {
          const updated = [...prev];
          updated[existingIdx] = { ...updated[existingIdx], qty: updated[existingIdx].qty + newCartItem.qty };
          return updated;
        }
        return [...prev, newCartItem];
      });

    } else {
      // Simple item without customizations
      setCart(prev => {
        const existingIdx = prev.findIndex(c => c.itemId === item.id && !c.selectedAddons?.length && !c.selectedModifiers?.length && !c.specialNote);
        if (existingIdx >= 0) {
          const updated = [...prev];
          updated[existingIdx] = { ...updated[existingIdx], qty: updated[existingIdx].qty + 1 };
          return updated;
        }
        return [...prev, {
          cartItemId: Date.now() + Math.random().toString(36).slice(2),
          itemId: item.id,
          name: item.name,
          price: item.price,
          imageUrl: item.imageUrl,
          isVeg: item.isVeg,
          categoryIsMrp: Boolean(item.categoryIsMrp ?? item.category_is_mrp ?? item.isMrp ?? item.is_mrp ?? false),
          qty: 1,
          selectedAddons: [],
          selectedModifiers: [],
          specialNote: ''
        }];
      });

    }
  };

  const removeFromCart = (cartItem) => {
    if (!cartItem) return;
    setCart(prev => {
      const idx = prev.findIndex(c => c.cartItemId === cartItem.cartItemId);
      if (idx < 0) return prev;
      const updated = [...prev];
      if (updated[idx].qty > 1) {
        updated[idx] = { ...updated[idx], qty: updated[idx].qty - 1 };
      } else {
        updated.splice(idx, 1);
        toast.success(`${cartItem?.name || 'Item'} removed from cart`, { duration: 1000, icon: '🗑️' });
      }
      return updated;
    });
  };

  const incrementCartItem = (cartItem) => {
    if (!cartItem) return;
    setCart(prev => {
      const idx = prev.findIndex(c => c.cartItemId === cartItem.cartItemId);
      if (idx < 0) return prev;
      const updated = [...prev];
      updated[idx] = { ...updated[idx], qty: updated[idx].qty + 1 };
      return updated;
    });
  };

  const cartTotalItems = cart.reduce((sum, c) => sum + (c.qty || 0), 0);
  
  const cartTotalPrice = cart.reduce((total, c) => total + getCartItemTotal(c), 0);

  // -- Filtering --
  const filteredItems = useMemo(() => {
    return menuItems.filter(item => {
      if (!item || !item.name) return false;
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesVeg = vegOnly ? item.isVeg : true;
      const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
      return matchesSearch && matchesVeg && matchesCategory;
    });
  }, [searchTerm, vegOnly, activeCategory, menuItems]);

  const itemsByCategory = useMemo(() => {
    const grouped = {};
    const categoriesToShow = activeCategory === 'All' ? categories.filter(c => c !== 'All') : [activeCategory];
    
    categoriesToShow.forEach(cat => {
      const items = filteredItems.filter(i => i.category === cat);
      if (items.length > 0) grouped[cat] = items;
    });
    return grouped;
  }, [filteredItems, activeCategory, categories]);

  const scrollToCategory = (cat) => {
    setActiveCategory(cat);
    if (cat !== 'All') {
      const element = document.getElementById(`cat-${cat}`);
      if (element) {
        const headerOffset = 120;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    }
  };

  const handlePlaceOrder = async () => {
    if (cartTotalItems === 0) {
      toast.error('Cart is empty!');
      return;
    }
    
    // Show customer form
    setIsCartOpen(false);
    setShowCustomerForm(true);
  };

  const handleSendOTP = async () => {
    const { name, phone, email } = customerData;
    
    // For staff orders - if no customer details provided, place order directly without OTP.
    // BUT — if there is already an active session on this table, we must NOT create a new order.
    // Instead, redirect back to the cart so the "Add to Order" button is used.
    if (!phone.trim() || !name.trim()) {
      if (hasActiveSession && existingOrder) {
        // Already has an active order on this table — adding items, not creating new order
        setShowCustomerForm(false);
        setIsCartOpen(true);
        return;
      }
      // No active session — place new order directly without customer details
      await placeOrderDirectly();
      return;
    }
    
    // Validate email if provided
    if (email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error('Please enter a valid email');
        return;
      }
    }
    
    // Validate phone (basic validation for Indian numbers)
    if (phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    // Save phone for session management
    localStorage.setItem('customerPhone', phone.trim());
    
    // Check for existing session first
    const sessionExists = await checkCustomerSession(phone.trim());
    if (sessionExists) {
      toast.success('Welcome back! You can add items to your existing order.');
      setShowCustomerForm(false);
      return;
    }
    
    setIsOTPSending(true);
    
    try {
      const token = localStorage.getItem('staffToken');
      const response = await axios.post(
        `${API_URL}/api/restaurants/${restaurantId}/qr-orders/send-otp`,
        {
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim()
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        setCustomerId(response.data.customerId);
        setShowCustomerForm(false);
        setShowOTPForm(true);
        setOtpCooldown(30); // Start 30 second cooldown
        toast.success('OTP sent to your email!', { duration: 3000 });
      } else {
        toast.error('Failed to send OTP');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setIsOTPSending(false);
    }
  };
  
  // Direct order placement without customer details (for staff)
  const placeOrderDirectly = async () => {
    setIsOrderPlacing(true);
    try {
      const items = cart.map(cartItem => ({
        itemId: cartItem.itemId,
        itemName: cartItem.name,
        quantity: cartItem.qty,
        price: getCartItemUnitPrice(cartItem),
        specialNote: buildKotSpecialRequestJson(cartItem),
        categoryIsMrp: Boolean(cartItem.categoryIsMrp ?? cartItem.category_is_mrp ?? cartItem.isMrp ?? cartItem.is_mrp)
      }));

      const taxAmount = calculateTax(getTaxableCartTotal());
      const token = localStorage.getItem('staffToken');
      const response = await axios.post(
        `${API_URL}/api/restaurants/${restaurantId}/qr-orders/place-without-customer`,
        {
          tableId: parseInt(tableId),
          items: items,
          numberOfPersons: customerData.numberOfPersons || 1,
          subtotal: cartTotalPrice,
          taxTotal: taxAmount,
          grandTotal: cartTotalPrice + taxAmount
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        console.log('✅ Staff order placed successfully:', response.data);

        // Use backend-calculated grandTotal (MRP-aware, correct tax)
        const grandTotal = response.data.grandTotal || (cartTotalPrice + calculateTax(getTaxableCartTotal()));

        // Create session for this table
        const orderData = {
          orderId: response.data.orderId,
          orderNumber: response.data.orderNumber,
          customerId: null, // No customer for staff orders
          grandTotal: grandTotal
        };
        
        // Store session in localStorage for persistence (table-based)
        localStorage.setItem('activeTableOrderId', response.data.orderId.toString());
        localStorage.setItem('activeOrderNumber', response.data.orderNumber);
        
        setHasActiveSession(true);
        setExistingOrder(orderData);
        setOrderResult({
          success: true,
          orderId: response.data.orderId,
          orderNumber: response.data.orderNumber,
          kotNumber: response.data.kotNumber,
          estimatedTime: '15-20 min'
        });
        setOrderPlaced(true);
        setShowCustomerForm(false);
        setCart([]);
        
        console.log('✅ Table session created and saved to localStorage:', {
          tableId,
          orderId: response.data.orderId,
          orderNumber: response.data.orderNumber
        });
        
        toast.success('🎉 Order placed successfully! You can add more items anytime.', { duration: 5000 });
      } else {
        toast.error('Failed to place order');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setIsOrderPlacing(false);
    }
  };
  
  const handleVerifyOTP = async () => {
    if (!otp.trim() || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    setIsOrderPlacing(true);
    
    try {
      const orderItems = cart.map(cartItem => ({
        itemId: cartItem.itemId,
        itemName: cartItem.name,
        quantity: cartItem.qty,
        price: getCartItemUnitPrice(cartItem),
        specialNote: buildKotSpecialRequestJson(cartItem),
        categoryIsMrp: Boolean(cartItem.categoryIsMrp ?? cartItem.category_is_mrp ?? cartItem.isMrp ?? cartItem.is_mrp)
      }));

      const taxAmount = calculateTax(getTaxableCartTotal());
      
      const orderData = {
        customerId: customerId,
        otp: otp.trim(),
        tableId: parseInt(tableId),
        numberOfPersons: customerData.numberOfPersons,
        items: orderItems,
        subtotal: cartTotalPrice,
        taxTotal: taxAmount,
        discount: 0,
        grandTotal: cartTotalPrice + taxAmount,
        specialInstructions: ''
      };

      const token = localStorage.getItem('staffToken');
      const response = await axios.post(
        `${API_URL}/api/restaurants/${restaurantId}/qr-orders/verify-and-place-order`,
        orderData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        console.log('Order placed successfully, storing session data:', response.data);
        
        // Create order object from response data
        const orderData = {
          orderId: response.data.orderId,
          orderNumber: response.data.orderNumber,
          customerId: response.data.order?.customerId || customerData.id || customerId,
          customerName: customerData.name,
          estimatedTime: response.data.estimatedTime,
          grandTotal: response.data.order?.grandTotal || cartTotalPrice // Use order grand total or cart total as fallback
        };
        
        // Store customer session in localStorage for persistence
        localStorage.setItem('customerPhone', customerData.phone);
        localStorage.setItem('customerId', (response.data.order?.customerId || customerData.id || customerId).toString());
        localStorage.setItem('customerName', customerData.name);
        localStorage.setItem('activeOrderNumber', response.data.orderNumber);
        
        setOrderResult(response.data);
        setExistingOrder(orderData); // Set as existing order for session
        setHasActiveSession(true); // Enable session mode
        setShowOTPForm(false);
        setOrderPlaced(true);
        setCart([]);
        setSearchTerm("");
        setCustomerData({ name: '', phone: '', email: '', numberOfPersons: 1 });
        
        console.log('Session data saved to localStorage:', {
          phone: customerData.phone,
          customerId: response.data.order?.customerId || customerData.id || customerId,
          orderNumber: response.data.orderNumber
        });
        
        toast.success('🎉 Order placed successfully! You can add more items anytime.', { duration: 5000 });
      } else {
        toast.error(response.data.error || 'Failed to verify OTP');
      }
      
    } catch (error) {
      console.error('Error verifying OTP:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to place order. Please try again.');
      }
    } finally {
      setIsOrderPlacing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-red-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading menu...</p>
        </div>
      </div>
    );
  }

  if (!restaurantInfo || !tableInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Invalid QR Code</h2>
          <p className="text-gray-600">Please scan a valid table QR code</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-orange-50 via-white to-gray-50 min-h-screen pb-24 font-sans text-gray-800">
      {/* --- Top Header --- */}
      <div className="bg-gradient-to-r from-orange-500 via-orange-600 to-orange-700 shadow-lg sticky top-0 z-40">
        <div className="p-3 sm:p-4 flex flex-col gap-2 sm:gap-3">
          <p className="text-center text-[10px] sm:text-xs font-bold text-orange-100/95 tracking-wide">
            Powered by Trivana Hospitality
          </p>
          {/* Brand */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-base sm:text-lg font-bold leading-tight text-white drop-shadow-sm">{restaurantInfo.name}</h1>
                <p className="text-xs text-orange-100 font-medium">{restaurantInfo.cuisineType || 'Multi-Cuisine'}</p>
              </div>
            </div>

            {/* Table Number Badge */}
            <div className="flex items-center gap-2">
              <div className="bg-white/20 backdrop-blur-sm text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-xs sm:text-sm font-bold shadow-lg tracking-wider border border-white/30">
                {tableInfo.tableNumber || tableId}
              </div>
            </div>
          </div>

          {/* Session Indicator */}
          {hasActiveSession && existingOrder && (
            <div className="bg-green-500/20 backdrop-blur-sm border border-green-400/30 px-3 py-2 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-100 font-medium">
                  Active Order: {existingOrder.orderNumber}
                </span>
              </div>
              <span className="text-xs text-green-200 font-bold">
                Total: ₹{existingOrder.grandTotal || 0}
              </span>
            </div>
          )}

          {/* Search */}
          <div className="flex gap-2 sm:gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-orange-200" size={16} />
              <input
                type="text"
                placeholder="Search for dishes..."
                className="w-full bg-white/30 backdrop-blur-sm border border-white/30 pl-9 pr-3 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm text-white placeholder-orange-100 focus:outline-none focus:ring-2 focus:ring-white/40 focus:bg-white/40 transition-all shadow-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Category Scroll */}
        {/* Moved to floating button at bottom */}
      </div>

      {/* --- Menu List --- */}
      <div className="px-3 py-4 space-y-4 max-w-2xl mx-auto pb-32">
        {Object.keys(itemsByCategory).length === 0 ? (
          <div className="text-center py-20">
            <div className="text-gray-300 mb-2 flex justify-center"><Search size={48}/></div>
            <p className="text-gray-500">No items found matching your search.</p>
          </div>
        ) : (
          Object.entries(itemsByCategory).map(([category, items]) => (
            <div key={category} id={`cat-${category}`} className="scroll-mt-32">
              <h2 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2 px-0.5">
                {category} <span className="bg-orange-100 text-orange-600 text-xs font-bold px-2 py-0.5 rounded-full">({items.length})</span>
              </h2>
              
              <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-200/50 divide-y divide-gray-100 overflow-hidden">
                {items.map(item => (
                  <div key={item.id} className="p-3 flex gap-3 hover:bg-orange-50/30 transition-colors">
                    {/* Item Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-1 mb-0.5">
                        <div className="mt-0.5">{item.isVeg ? <VegIcon /> : <NonVegIcon />}</div>
                        {item.isBestseller && (
                          <div className="bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md flex items-center gap-0.5 shadow-sm">
                            <Star size={8} fill="currentColor" /> BESTSELLER
                          </div>
                        )}
                      </div>
                      <h3 className="font-bold text-gray-900 mb-1 leading-snug text-sm truncate">{item.name}</h3>
                      {(() => {
                        const effective = Number(item.price) || 0;
                        if (effective === 0 && Array.isArray(item.modifierGroups) && item.modifierGroups.length > 0) {
                          const firstGroup = item.modifierGroups[0];
                          const opts = Array.isArray(firstGroup.options) ? firstGroup.options : [];
                          if (opts.length > 0) {
                            const prices = opts.map(o => getEffectiveOrderTypePrice(o.optionPrice ?? o.price ?? 0, o.orderTypePrices));
                            const min = Math.min(...prices);
                            const fmt = Number.isFinite(min) ? (min % 1 === 0 ? min : min.toFixed(2)) : 0;
                            return <div className="text-sm font-bold text-orange-600 mb-1">FROM ₹{fmt}</div>;
                          }
                        }
                        return <div className="text-sm font-bold text-orange-600 mb-1">₹{effective}</div>;
                      })()}
                      <p className="text-xs text-gray-600 leading-tight line-clamp-1">{item.description}</p>
                    </div>

                    {/* Action Area */}
                    <div className="flex flex-col items-center justify-start gap-2 min-w-[90px]">
                      <div className="w-24 h-20 bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg mb-0 relative overflow-hidden flex items-center justify-center shadow-sm border-2 border-white">
                        {item.imageUrl ? (
                          <img src={resolveMediaUrl(item.imageUrl)} alt={item.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                          <div className="opacity-20 text-orange-500">
                            <ChefHat size={28} />
                          </div>
                        )}
                      </div>
                      <div>
                        <AddButton 
                          count={cart.filter(c => c.itemId === item.id).reduce((sum, c) => sum + c.qty, 0)}
                          onAdd={() => {
                            if (hasCustomizations(item)) {
                              setCustomizationModal(item);
                            } else {
                              addToCart(item);
                            }
                          }}
                          onRemove={() => {
                            const cartItems = cart.filter(c => c.itemId === item.id);
                            if (cartItems.length > 0) {
                              removeFromCart(cartItems[cartItems.length - 1]);
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* --- Floating Cart Bar --- */}
      {cartTotalItems > 0 && (
        <div className="fixed bottom-20 left-4 right-4 z-50 max-w-2xl mx-auto animate-in slide-in-from-bottom-4 duration-300">
          <div className="bg-gradient-to-r from-orange-600 via-orange-500 to-red-600 rounded-2xl shadow-2xl p-4 flex items-center justify-between text-white ring-4 ring-orange-200">
            <div className="flex flex-col px-2">
              <span className="text-xs font-semibold text-orange-100 tracking-wide">{cartTotalItems} ITEM{cartTotalItems !== 1 && 'S'}</span>
              <span className="text-2xl font-extrabold drop-shadow-sm">₹{cartTotalPrice}</span>
            </div>
            <button 
              onClick={() => setIsCartOpen(true)}
              className="flex items-center gap-2 bg-white text-orange-600 px-5 py-3 rounded-xl font-bold text-sm hover:bg-orange-50 hover:scale-105 active:scale-95 transition-all shadow-lg"
            >
              View Cart <ShoppingBag size={18} />
            </button>
          </div>
        </div>
      )}

      {/* --- Cart Modal Overlay --- */}
      {isCartOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col justify-end sm:justify-center items-center bg-black/70 backdrop-blur-md p-0 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-md h-[85vh] sm:h-auto sm:max-h-[80vh] rounded-t-3xl sm:rounded-3xl flex flex-col shadow-2xl animate-in slide-in-from-bottom-10 duration-300 border-t-4 border-orange-500">
            
            {/* Modal Header */}
            <div className="p-5 border-b flex items-center justify-between bg-gradient-to-r from-orange-50 to-red-50 rounded-t-3xl">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Your Order</h2>
                <p className="text-xs text-gray-600 mt-0.5">{cartTotalItems} item{cartTotalItems !== 1 && 's'} added</p>
              </div>
              <button onClick={() => setIsCartOpen(false)} className="p-2.5 bg-white rounded-full hover:bg-gray-100 shadow-sm transition-all hover:scale-110">
                <X size={22} className="text-gray-600" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-gray-50">
               {cart.map(cartItem => {
                  const note = formatSpecialNote(cartItem);
                  return (
                    <div key={cartItem.cartItemId} className="flex items-center justify-between bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                       <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                             {cartItem.isVeg ? <VegIcon /> : <NonVegIcon />}
                             <span className="font-bold text-sm text-gray-900 truncate">{cartItem.name || 'Unknown Item'}</span>
                          </div>
                          <div className="text-xs text-gray-500 font-medium">₹{getCartItemUnitPrice(cartItem)} x {cartItem.qty}</div>
                          {note && (
                            <div className="mt-1 text-[10px] text-gray-500 bg-gray-50 px-2 py-1 rounded leading-tight">
                              {note}
                            </div>
                          )}
                       </div>
                       <div className="flex items-center gap-4 flex-shrink-0 ml-2">
                          <div className="font-bold text-orange-600 text-base">₹{getCartItemTotal(cartItem)}</div>
                          <AddButton 
                             count={cartItem.qty}
                             onAdd={() => incrementCartItem(cartItem)}
                             onRemove={() => removeFromCart(cartItem)}
                          />
                       </div>
                    </div>
                  );
               })}
               
               <div className="border-t-2 border-dashed border-gray-300 my-4 pt-5 px-1">
                 <div className="flex justify-between items-center text-sm mb-3 text-gray-700">
                   <span className="font-medium">Item Total</span>
                   <span className="font-semibold">₹{cartTotalPrice}</span>
                 </div>
                 <div className="flex justify-between items-center text-sm mb-4 text-gray-700">
                   <span className="font-medium">Taxes & Charges <span className="text-xs text-gray-500">({getTaxPercentageText()}%)</span></span>
                   <span className="font-semibold">₹{calculateTax(getTaxableCartTotal())}</span>
                 </div>
                 <div className="flex justify-between items-center font-bold text-xl pt-4 border-t-2 border-gray-200">
                   <span className="text-gray-900">Grand Total</span>
                   <span className="text-orange-600">₹{cartTotalPrice + calculateTax(getTaxableCartTotal())}</span>
                 </div>
               </div>
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t bg-white rounded-b-3xl shadow-inner">
              {/* Debug Info */}
              {console.log("🔍 BUTTON CONDITION DEBUG:", {
                hasActiveSession,
                existingOrder: existingOrder ? "exists" : "null",
                customerId,
                cartTotalItems,
                isOrderPlacing
              })}
              
              {hasActiveSession && existingOrder ? (
                // Show "Add to Order" for session customers
                <div className="space-y-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-700 font-medium text-center mb-2">
                      Adding to Order: {existingOrder.orderNumber}
                    </p>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-green-600">Current Total:</span>
                      <span className="font-bold text-green-700">₹{existingOrder?.grandTotal || 0}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-green-600">Adding:</span>
                      <span className="font-bold text-green-700">₹{cartTotalPrice + calculateTax(getTaxableCartTotal())}</span>
                    </div>
                    <hr className="my-1 border-green-200" />
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-green-700 font-medium">New Total:</span>
                      <span className="font-bold text-green-800">₹{((parseFloat(existingOrder?.grandTotal) || 0) + cartTotalPrice + calculateTax(getTaxableCartTotal())).toFixed(2)}</span>
                    </div>
                  </div>
                  <button 
                    onClick={() => { if (!canCreateOrder) return; addToExistingOrder(); }}
                    disabled={isOrderPlacing || !canCreateOrder}
                    className="w-full bg-gradient-to-r from-green-600 to-green-500 text-white font-bold py-4 rounded-xl shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 text-base disabled:opacity-50"
                  >
                    {isOrderPlacing ? (
                      <Loader className="w-5 h-5 animate-spin" />
                    ) : (
                      <Plus size={20} />
                    )}
                    {isOrderPlacing ? 'Adding...' : !canCreateOrder ? 'No Permission' : `Add to Order ₹${cartTotalPrice}`}
                  </button>
                </div>
              ) : (
                // Show "Place Order" for new customers
                <button 
                  onClick={() => { if (!canCreateOrder) return; handlePlaceOrder(); }}
                  disabled={!canCreateOrder}
                  className={`w-full bg-gradient-to-r from-orange-600 via-orange-500 to-red-600 text-white font-bold py-4 rounded-xl shadow-xl transition-all flex items-center justify-center gap-2 text-base ${canCreateOrder ? 'hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98]' : 'opacity-50 cursor-not-allowed'}`}
                >
                  <ShoppingBag size={20} />
                  {canCreateOrder ? `Place Order ₹${cartTotalPrice + calculateTax(getTaxableCartTotal())}` : 'No Permission to Create Order'}
                </button>
              )}
            </div>

          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200">
        <div className="max-w-md mx-auto px-3 py-2 flex justify-around items-center">
          <button 
            onClick={() => navigate('/tables')}
            className="flex flex-col items-center gap-0.5 text-gray-500 hover:text-orange-600"
          >
            <Home className="w-5 h-5" />
            <span className="text-xs">Home</span>
          </button>
          <button 
            className="flex flex-col items-center gap-0.5 text-orange-600"
          >
            <Menu className="w-5 h-5" />
            <span className="text-xs font-medium">Menu</span>
          </button>
          <button 
            onClick={() => navigate(`/orders?r=${restaurantId}&t=${tableId}`)}
            className="flex flex-col items-center gap-0.5 text-gray-500 hover:text-orange-600"
          >
            <ShoppingBag className="w-5 h-5" />
            <span className="text-xs">Orders</span>
          </button>
          <button 
            onClick={() => navigate(`/pay-bill?r=${restaurantId}&t=${tableId}`)}
            className="flex flex-col items-center gap-0.5 text-gray-500 hover:text-orange-600"
          >
            <Receipt className="w-5 h-5" />
            <span className="text-xs">Pay Bill</span>
          </button>
        </div>
      </div>
      
      {/* Customer Form Modal */}
      {showCustomerForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ChefHat className="w-8 h-8 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Customer Details</h2>
              <p className="text-gray-600">Optional - Skip to place order directly</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name (Optional)</label>
                <input
                  type="text"
                  placeholder="Enter customer name (optional)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  value={customerData.name}
                  onChange={(e) => setCustomerData({...customerData, name: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number (Optional)</label>
                <input
                  type="tel"
                  placeholder="Enter phone number (optional)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  value={customerData.phone}
                  onChange={(e) => setCustomerData({...customerData, phone: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address (Optional)</label>
                <input
                  type="email"
                  placeholder="Enter email (optional)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  value={customerData.email}
                  onChange={(e) => setCustomerData({...customerData, email: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">OTP will be sent if email is provided</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Number of Persons</label>
                <select
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  value={customerData.numberOfPersons}
                  onChange={(e) => setCustomerData({...customerData, numberOfPersons: parseInt(e.target.value)})}
                >
                  {[1,2,3,4,5,6,7,8,9,10].map(num => (
                    <option key={num} value={num}>{num} {num === 1 ? 'Person' : 'Persons'}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex flex-col gap-2 mt-6">
              <button
                onClick={() => { if (!canCreateOrder) return; placeOrderDirectly(); }}
                disabled={isOrderPlacing || !canCreateOrder}
                className="w-full px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isOrderPlacing ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Placing Order...
                  </>
                ) : !canCreateOrder ? (
                  'No Permission to Create Order'
                ) : (
                  <>
                    <ShoppingBag className="w-4 h-4" />
                    Skip & Place Order
                  </>
                )}
              </button>
              
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowCustomerForm(false);
                    setSearchTerm("");
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendOTP}
                  disabled={isOTPSending || otpCooldown > 0}
                  className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isOTPSending ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : otpCooldown > 0 ? (
                    `Wait ${otpCooldown}s`
                  ) : (
                    'Send OTP'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* OTP Verification Modal */}
      {showOTPForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Verify Email</h2>
              <p className="text-gray-600">
                We've sent a 6-digit OTP to <br />
                <span className="font-semibold text-gray-800">{customerData.email}</span>
              </p>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Enter OTP</label>
              <input
                type="text"
                placeholder="Enter 6-digit OTP"
                maxLength="6"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-center text-2xl font-mono tracking-widest"
                value={otp}
                onChange={(e) => setOTP(e.target.value.replace(/\D/g, ''))}
              />
              <p className="text-xs text-gray-500 mt-1 text-center">OTP will expire in 10 minutes</p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowOTPForm(false)}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Back
              </button>
              <button
                onClick={handleVerifyOTP}
                disabled={isOrderPlacing || otp.length !== 6}
                className="flex-1 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isOrderPlacing ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Placing...
                  </>
                ) : (
                  'Place Order'
                )}
              </button>
            </div>
            
            <button 
              onClick={handleSendOTP}
              disabled={isOTPSending || otpCooldown > 0}
              className="w-full mt-4 text-sm text-orange-600 hover:text-orange-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isOTPSending ? 'Sending...' : otpCooldown > 0 ? `Resend OTP in ${otpCooldown}s` : 'Resend OTP'}
            </button>
          </div>
        </div>
      )}
      
      {/* Order Success Modal */}
      {orderPlaced && orderResult && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">🎉</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Order Placed!</h2>
              <p className="text-gray-600">{orderResult.message}</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Order ID:</span>
                  <div className="font-bold text-orange-600">#{orderResult.orderId}</div>
                </div>
                <div>
                  <span className="text-gray-600">Estimated Time:</span>
                  <div className="font-bold text-green-600">{orderResult.estimatedTime}</div>
                </div>
                {orderResult.customer && (
                  <div className="col-span-2">
                    <span className="text-gray-600">Customer:</span>
                    <div className="font-medium">{orderResult.customer.name}</div>
                    <div className="text-xs text-gray-500">{orderResult.customer.phone}</div>
                  </div>
                )}
              </div>
            </div>
            
            <button
              onClick={() => setOrderPlaced(false)}
              className="w-full px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium"
            >
              Continue Browsing
            </button>
          </div>
        </div>
      )}

      {/* Customization Modal */}
      {customizationModal && (
        <CustomizationModal
          item={customizationModal}
          onConfirm={({ item, qty, selectedAddons, selectedModifiers, specialNote }) => {
            addToCart(item, { qty, selectedAddons, selectedModifiers, specialNote });
            setCustomizationModal(null);
          }}
          onCancel={() => setCustomizationModal(null)}
        />
      )}

      {/* --- Floating Categories Button --- */}
      {categories.length > 0 && !showCustomerForm && !showOTPForm && !orderPlaced && !isCartOpen && (
        <button
          onClick={() => setShowCategoryMenuModal(true)}
          className={`fixed z-[55] px-4 py-2 bg-black text-white rounded-full shadow-2xl flex items-center justify-center gap-2 hover:scale-110 active:scale-95 transition-all border-3 border-orange-500 hover:shadow-3xl font-semibold text-sm ${
            cartTotalItems > 0 ? 'bottom-36 left-1/2 -translate-x-1/2' : 'bottom-20 right-4'
          }`}
          title="Filter by Category"
        >
          <Menu size={20} strokeWidth={2} />
          Menu
        </button>
      )}

      {/* --- Category Menu Modal --- */}
      {showCategoryMenuModal && (
        <div className="fixed inset-0 z-[65] flex items-end sm:items-center justify-center sm:justify-end p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full sm:w-80 h-auto max-h-[70vh] rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col animate-in slide-in-from-bottom-4 sm:slide-in-from-right-4 duration-300 m-4">
            
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b bg-gradient-to-r from-orange-500 to-red-600 rounded-t-3xl flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">
                Categories
              </h3>
              <button 
                onClick={() => setShowCategoryMenuModal(false)} 
                className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-all"
              >
                <X size={20} className="text-white" />
              </button>
            </div>

            {/* Categories List */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    scrollToCategory(cat);
                    setShowCategoryMenuModal(false);
                  }}
                  className={`w-full px-4 py-3 rounded-xl font-semibold transition-all text-left ${
                    activeCategory === cat 
                      ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg scale-[1.02]' 
                      : 'bg-gray-100 text-gray-900 hover:bg-orange-100 hover:text-orange-600'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
